import React from 'react';
import Head from 'next/head';
import Layout from '../layout/layout';
import settings from '../../settings.json';
import { getAbsoluteMediaURL } from '../../lib/media';
import { default as Components, ComponentMappings } from '../blocks/components';
import { P, Row, Column, H1, H5, Button } from '../styleguide-remapped/styleguide-remapped';
import { uid } from 'react-uid';
import { HeadlineWrapper } from '../blocks/headline/style';
import MenuWrapper from './menu-wrapper';
import ReactHtmlParser from 'react-html-parser';
import Breadcrumbs from './breadcrumbs';
import { ParagraphWrapper } from '../blocks/paragraph/style';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { PrimaryLayoutWrapper } from '../../assets/css/style';

const Article = ({ categories, topics, article, is404 }) => {
  const pageType = 'ARTICLE';
  const isFaq = article.attributes.isFaq ? true : false;

   const getHeadlineText = (node) => {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(getHeadlineText).join('');
    return getHeadlineText(node.props?.children);
  };

  var anchorList = [];
  let seenH4 = false;
  article.attributes.blockContent = article.attributes.blockContent.map((block) => {
    let counter = 0;
    if (block.__component == 'block.paragraph') {
      var hList = ReactHtmlParser(block.content).filter((e) => e.type === 'h4' || e.type === 'h5');
      block.headingLevels = {};
      hList.forEach((h) => {
        const index = counter++;
        const id = `${block.__component}-${block.id}-${index}`;
        const title = getHeadlineText(h.props?.children).trim();

        if (h.type === 'h4') seenH4 = true;
        const level = h.type === 'h4' || !seenH4 ? 'h2' : 'h3';
        block.headingLevels[index] = level;

        if (title) {
          anchorList.push({ id, title, type: h.type, level });
        }
      });
    }

    return block;
  });

  const getArticleStructuredData = (article) => {
    const result = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      name: article.attributes.title,
      description: article.attributes.description,
      headline: article.attributes.title,
      image: [],
      datePublished: article.attributes.publishedAt,
      dateModified: article.attributes.updatedAt,
      author: [],
      publisher: [
        {
          name: 'ARD.de',
          url: 'https://hilfe.ard.de',
        },
      ],
    };

    article.attributes.authors?.data?.forEach((author) => {
      const authorJson = {
        '@type': author.attributes.isOrganization ? 'Organization' : 'Person',
        name: author.attributes.name,
        url: author.attributes.authorUrl,
      };

      result.author.push(authorJson);
    });

    const metaImageUrl = getAbsoluteMediaURL(article.attributes?.seo?.metaImage?.data?.attributes?.url);
    if (metaImageUrl) {
      result.image.push(metaImageUrl);
    }

    return result;
  };

  const getCanonical = () => {
    const router = useRouter();
    const currentLocation = `https://${APP_SETTINGS.appUris.slice(-1)[0]}${router.asPath}`;
    const path = usePathname();
    const topicsForArticle = [];
    const categoriesForArticle = [];

    topics.forEach((relatedTopic) => {
      relatedTopic?.attributes?.articles?.forEach((topicSection) => {
        if (topicSection.articles?.data?.find((d) => d.id === article.id) && !topicsForArticle.find((d) => d.id === relatedTopic.id)) {
          topicsForArticle.push(relatedTopic);
        }
      });
    });

    topicsForArticle.forEach((topicForArticle) => {
      if (topicForArticle?.attributes?.category?.data?.id && !categoriesForArticle.find((d) => d.id === topicForArticle.attributes.category.data.id)) {
        categoriesForArticle.push(topicForArticle.attributes.category.data);
      }
    });

    const splittedPath = path.replace(/\/$/, '').split('/').filter(Boolean);

    if (splittedPath?.length > 0 && (splittedPath[0] === 'artikel' || splittedPath?.length === 3)) {
      if (topicsForArticle.length > 1 || categoriesForArticle.length > 1) {
        return `https://${APP_SETTINGS.appUris.slice(-1)[0]}/artikel/${splittedPath.pop()}/`;
      } else if (topicsForArticle.length === 1 && categoriesForArticle.length === 1) {
        return `https://${APP_SETTINGS.appUris.slice(-1)[0]}/${categoriesForArticle[0].attributes.slug}/${topicsForArticle[0].attributes.slug}/${splittedPath.pop()}/`;
      }
    }

    return currentLocation;
  };

  if (article.attributes.contactCta?.id) {
    anchorList.push({ id: 'block.contact-cta-' + article.attributes.contactCta.id, title: 'Kontakt' });
  }
  if (article.attributes.relatedArticles?.id && article.attributes.relatedArticles.headline?.trim()) {
    anchorList.push({ id: 'block.related-articles-1-column-' + article.attributes.relatedArticles.id, title: article.attributes.relatedArticles.headline });
  }

  return (
    <>
      <Head>
        <link rel="canonical" href={getCanonical()} />
        {!isFaq && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getArticleStructuredData(article)) }} />}
      </Head>
      <Layout seo={article.attributes.seo} key={uid(article)} hideSearch={false} pageType={pageType} documentData={article} is404={is404}>
        <PrimaryLayoutWrapper>
          <Row>
            <Column all={12} xs={12} s={12} m={3}>
              <MenuWrapper as="nav" aria-label="Inhalt">
                <H5 as="div" className="menu-label">SPRINGE ZU KAPITEL</H5>
                {anchorList.filter(d => d.level !== 'h3').map((anchor) => (
                  <div key={anchor.id}>
                    <Button type="text" text={anchor.title} href={'#' + anchor.id} />
                  </div>
                ))}
              </MenuWrapper>
            </Column>
            <Column all={12} xs={12} s={12} m={9}>
              <Row className="article-breadcrumb-row">
                <Breadcrumbs categories={categories} topics={topics} articles={[article]} className="primary-link" breadcrumbKey={`${uid(article)}-b`} />
              </Row>
              <HeadlineWrapper>
                <H1 className="h2"> {article.attributes.title} </H1>
              </HeadlineWrapper>
              <ParagraphWrapper className="article-description">
                <P>{article.attributes.description}</P>
              </ParagraphWrapper>
              {article.attributes.blockContent && article.attributes.blockContent.length
                ? article.attributes.blockContent.map((block, index) => {
                  block.id = `${block.id}`;
                  block.mappingIndex = index;

                  if (settings.skipableElements.includes(block.__component)) {
                    return false;
                  }

                  return Components(block, article, pageType, categories, topics);
                })
                : null}
              {React.createElement(ComponentMappings['block.contact-cta'], {
                key: `comp-${uid(article.attributes.contactCta)}`,
                block: {
                  ...{
                    __component: 'block.contact-cta',
                    id: 'block.contact-cta-' + uid(article.attributes.contactCta.id),
                    mappingIndex: `comp-${uid(article.attributes.contactCta.id)}`,
                  },
                  ...article.attributes.contactCta,
                },
                documentData: article,
                pageType: pageType,
                relatedCategories: categories ?? [],
                relatedTopics: topics ?? [],
              })}
              {React.createElement(ComponentMappings['block.related-articles-1-column'], {
                key: `comp-${uid(article.attributes.relatedArticles)}`,
                block: {
                  ...{
                    __component: 'block.related-articles-1-column',
                    id: 'block.related-articles-1-column-' + uid(article.attributes.relatedArticles.id),
                    mappingIndex: `comp-${uid(article.attributes.relatedArticles.id)}`,
                  },
                  ...article.attributes.relatedArticles,
                },
                documentData: article,
                pageType: pageType,
                relatedCategories: categories ?? [],
                relatedTopics: topics ?? [],
              })}
            </Column>
          </Row>
        </PrimaryLayoutWrapper>
      </Layout>
    </>
  );
};

export default Article;
